import React, { useEffect, useMemo, useState } from 'react';
import { UncontrolledTooltip } from 'reactstrap';
import withRouter from '../.././components/withRouter';
import TableContainer from '../.././components/TableContainerReactTable';
import ImportCampaignModal from './ImportCampaignModal';
import { Campaign } from '../../types/campaign';
import { format } from 'date-fns';
import { saveAs } from 'file-saver';
import { exportExcelCampaign, forceCacheUpdate, getCampaigns } from '../../api/campaignApi';
import { getOrderStatusColor } from '../Order';
import EditCampaignModal from './EditCampaignModal';
import UpdateCampaignClickTagModal from './UpdateCampaignClickTagModal';
import { toast } from 'react-toastify';
// import Cookies from 'js-cookie';
import { useProfile } from '../../hooks/UserHooks';
import { useNavigate } from 'react-router-dom';
import BulkActionStatusCampaignModal from './BulkActionStatusCampaignModal';

const Index = (props: any) => {
    useEffect(() => {
        getCampaigns().then(setData).finally(() => { setLoading(false); });
    }, []);

    const navigate = useNavigate();
    const { userProfile } = useProfile();
    const [data, setData] = useState<Campaign[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [isImportCampaignModalOpen, setIsImportCampaignModalOpen] = useState<boolean>(false);
    const [dataToEdit, setDataToEdit] = useState<Campaign|null>(null);
    const [isEditCampaignModalOpen, setIsEditCampaignModalOpen] = useState<boolean>(false);
    const [isUpdateCampaignClickTagModalOpen, setIsUpdateCampaignClickTagModalOpen] = useState<boolean>(false);
    const [isForceCacheUpdateLoading, setIsForceCacheUpdateLoading] = useState<boolean>(false);
    const [isBulkActionStatusCampaignModalOpen, setIsBulkActionStatusCampaignModalOpen] = useState<boolean>(false);

    const handleUpdateData = (newData: Campaign) => {
        const updatedData = data.map((item) => {
            if (item.id === newData.id) {
                return newData;
            }
            return item;
        });
        setData(updatedData);
    };

    const handleAddData = (newData: Campaign) => {
        setData([...data, newData]);
    };

    const toggleImportCampaignModal = () => {
        setIsImportCampaignModalOpen(!isImportCampaignModalOpen);
        updateCampaignList();
    };

    const toggleUpdateCampaignClickTagModal = () => {
        setIsUpdateCampaignClickTagModalOpen(!isUpdateCampaignClickTagModalOpen);
    };

    const toggleEditCampaignModal = () => {
        setIsEditCampaignModalOpen(!isEditCampaignModalOpen);
    };

    const toggleBulkActionStatusCampaignModal = () => {
        setIsBulkActionStatusCampaignModalOpen(!isBulkActionStatusCampaignModalOpen);
    }

    const updateCampaignList = () => {
        setLoading(true);
        getCampaigns().then(setData).finally(() => { setLoading(false); });
    }

    const forceCacheUpdateTrigger = () => {
        setIsForceCacheUpdateLoading(true);
        forceCacheUpdate().then(() => {
            toast.success('Cache atualizado com sucesso.');
        }).finally(() => { setIsForceCacheUpdateLoading(false); });
    }

    const statusTranslations: { [key: string]: string } = {
        'DRAFT': 'Rascunho',
        'NEEDS_CREATIVES': 'Necessita de Criativos',
        'NEEDS_CLICK_TAG': 'Necessita de URL de Clique',
    };

    const columns = useMemo(
        () => [
            {
                header: 'ID',
                cell: (cell: any) => {
                    return (
                        <span className="fw-semibold">{cell.getValue()}</span>
                    );
                },
                accessorKey: 'id',
                enableColumnFilter: false,
                enableSorting: true,
            },
            {
                header: 'Nome',
                accessorKey: 'name',
                enableColumnFilter: false,
                enableSorting: true,
            },
            {
                header: 'Anunciante',
                accessorFn: (row: any) => {
                    return row.advertiser?.name || '-';
                },
                enableColumnFilter: false,
                enableSorting: true,
            },
            {
                header: "Proteções",
                accessorKey: 'blockedKeywords.length',
                enableColumnFilter: false,
            },
            {
                header: 'Agência',
                accessorFn: (row: any) => {
                    return row.agency?.name || '-';
                },
                enableColumnFilter: false,
                enableSorting: true,
            },
            {
                header: 'Pedidos',
                cell: (cell: any) => {
                    const orders = cell.row.original.orders;
                    const statusCounts = orders.reduce((acc: any, order: any) => {
                        order.status.forEach((status: string) => {
                            acc[status] = (acc[status] || 0) + 1;
                        });
                        return acc;
                    }, {});

                    return Object.entries(statusCounts)
                        .map(([status, count]) => (
                            <>
                                <span key={status}
                                      className={`badge bg-${getOrderStatusColor(status)}`}>{`${statusTranslations[status] ?? status} (${count})`}</span>
                                <br />
                            </>
                        ));
                },
                enableColumnFilter: false,
            },
            {
                header: 'Data de Início',
                accessorFn: (row: any) => {
                    // Campaigns em ordem crescente de data de início
                    const campaigns = row.orders.sort((a: any, b: any) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
                    return campaigns[0]?.startDate ? format(new Date(campaigns[0].startDate), 'dd/MM/yyyy') : '-';
                },
                enableColumnFilter: false,
            },
            {
                header: 'Data de Termino',
                accessorFn: (row: any) => {
                    // Campaigns em ordem decrescente de data de término
                    const campaigns = row.orders.sort((a: any, b: any) => new Date(b.endDate).getTime() - new Date(a.endDate).getTime());
                    return campaigns[0]?.endDate ? format(new Date(campaigns[0].endDate), 'dd/MM/yyyy') : '-';
                },
                enableColumnFilter: false,
            },
            {
                id: 'actions',
                cell: (cell: any) => {
                    const row = cell.row.original;
                    let actionCell = [];

                    actionCell.push(<div key="action_report">
                        <button className="btn text-primary d-inline-block"
                                id={'action_report_' + row.id}
                                onClick={() => {
                                    navigate(`/campaign/report/${row.id}`);
                                }}
                        ><i className="ri-bar-chart-box-fill fs-16"></i></button>
                        <UncontrolledTooltip
                            placement="top"
                            target={'action_report_' + row.id}
                        >
                            Relatórios
                        </UncontrolledTooltip>
                    </div>);

                    // Botão de editar
                    if (userProfile?.role === 'ROLE_SUPER_ADMIN') {
                        actionCell.push(<div key="action_edit">
                            <button className="btn text-primary d-inline-block"
                                    id={'action_edit_' + row.id}
                                    onClick={() => {
                                        setDataToEdit(row);
                                        toggleEditCampaignModal();
                                    }}
                            ><i className="ri-pencil-fill fs-16"></i></button>
                            <UncontrolledTooltip
                                placement="top"
                                target={'action_edit_' + row.id}
                            >
                                Editar
                            </UncontrolledTooltip>
                        </div>);
                    }

                    // Botão para exportar para excel
                    actionCell.push(<div key="action_export">
                        <button className="btn text-success d-inline-block"
                                id={'action_export_' + row.id}
                                onClick={() => {
                                    exportExcelCampaign(row.id)
                                        .then((res) => {
                                            saveAs(res, `${row.id} - ${row.name}.xlsx`);
                                        })
                                        .catch(() => {
                                        });
                                }}
                        ><i className="ri-file-excel-2-fill fs-16"></i></button>
                        <UncontrolledTooltip
                            placement="top"
                            target={'action_export_' + row.id}
                        >
                            Exportar
                        </UncontrolledTooltip>
                    </div>);

                    // Botão para atualizar a url de clique das campaigns
                    if (userProfile?.role === 'ROLE_SUPER_ADMIN') {
                        actionCell.push(<div key="action_update_click_tag">
                            <button className="btn text-success d-inline-block"
                                    id={'action_update_click_tag_' + row.id}
                                    onClick={() => {
                                        setDataToEdit(row);
                                        toggleUpdateCampaignClickTagModal();
                                    }}
                            ><i className="ri-link fs-16"></i></button>
                            <UncontrolledTooltip
                                placement="top"
                                target={'action_update_click_tag_' + row.id}
                            >
                                Atualizar URL de clique
                            </UncontrolledTooltip>
                        </div>);
                    }

                    // Botão para atualizar o status do pedido em massa
                    if (userProfile?.role === 'ROLE_SUPER_ADMIN') {
                        actionCell.push(<div key="action_update_campaign_status">
                            <button className="btn text-success d-inline-block"
                                    id={'action_update_campaign_status_' + row.id}
                                    onClick={() => {
                                        setDataToEdit(row);
                                        toggleBulkActionStatusCampaignModal();
                                    }}
                            ><i className="ri-loop-left-line fs-16"></i></button>
                            <UncontrolledTooltip
                                placement="top"
                                target={'action_update_campaign_status_' + row.id}
                            >
                                Atualização de status em massa
                            </UncontrolledTooltip>
                        </div>);
                    }

                    return <div className="d-flex gap-2">{actionCell}</div>;
                },
                enableColumnFilter: false,
                enableSorting: false,
            },

        ],
        [],
    );

    document.title = 'Campanhas | Ad Pulse';
    return (
        <React.Fragment>
            <div className="page-content">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="card">
                                <div className="card-header">
                                    <div className="row g-4 align-items-center">
                                        <div className="col-sm">
                                            <div>
                                                <h5 className="card-title mb-0">Campanhas</h5>
                                            </div>
                                        </div>
                                        {userProfile?.role === 'ROLE_SUPER_ADMIN' && (
                                            <div className="col-sm-auto">
                                                <button
                                                    type="button"
                                                    className="btn btn-success add-btn me-1"
                                                    onClick={toggleImportCampaignModal}
                                                >
                                                    <i className="ri-add-line align-bottom me-1"></i> Criar campanha
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-secondary add-btn me-1"
                                                    onClick={forceCacheUpdateTrigger}
                                                >
                                                    <i className="ri-add-line align-bottom me-1"></i>
                                                    {isForceCacheUpdateLoading && <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>}
                                                    Atualizar cache
                                                </button>

                                                {/*<button*/}
                                                {/*    type="button"*/}
                                                {/*    className="btn btn-secondary add-btn me-1"*/}
                                                {/*    onClick={() => {*/}
                                                {/*        if (Cookies.get('debug')) {*/}
                                                {/*            Cookies.remove('debug');*/}
                                                {/*        } else {*/}
                                                {/*            Cookies.set('debug', 'true', {*/}
                                                {/*                expires: 90,*/}
                                                {/*                path: '/',*/}
                                                {/*                domain: '.adpulse.com.br',*/}
                                                {/*                httpOnly: true,*/}
                                                {/*                secure: true,*/}
                                                {/*                sameSite: 'None',*/}
                                                {/*            });*/}
                                                {/*        }*/}
                                                {/*    }}*/}
                                                {/*>*/}
                                                {/*    <i className="ri-add-line align-bottom me-1"></i>*/}
                                                {/*    {Cookies.get('debug') ? 'Disable Debug' : 'Enable Debug'}*/}
                                                {/*</button>*/}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="card-body">
                                    {!loading ? <TableContainer
                                            divClass="table-responsive"
                                            columns={(columns || [])}
                                            data={(data || [])}
                                            isGlobalFilter={true}
                                            customPageSize={30}
                                            SearchPlaceholder="Procurar..."
                                        /> :
                                        <div className="text-center">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="visually-hidden">Carregando...</span>
                                            </div>
                                        </div>}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {userProfile?.role === 'ROLE_SUPER_ADMIN' && (
                    <ImportCampaignModal
                        isOpen={isImportCampaignModalOpen}
                        toggle={toggleImportCampaignModal}
                    />
                )}

                {dataToEdit && userProfile?.role === 'ROLE_SUPER_ADMIN' && (
                    <EditCampaignModal
                        campaign={dataToEdit}
                        isOpen={isEditCampaignModalOpen}
                        toggle={toggleEditCampaignModal}
                        onUpdateData={handleUpdateData}
                    />
                )}

                {dataToEdit && userProfile?.role === 'ROLE_SUPER_ADMIN' && (
                    <UpdateCampaignClickTagModal
                        campaign={dataToEdit}
                        isOpen={isUpdateCampaignClickTagModalOpen}
                        toggle={toggleUpdateCampaignClickTagModal}
                        onUpdateData={handleUpdateData}
                    />
                )}

                {dataToEdit && userProfile?.role === 'ROLE_SUPER_ADMIN' && (
                    <BulkActionStatusCampaignModal
                        campaign={dataToEdit}
                        isOpen={isBulkActionStatusCampaignModalOpen}
                        toggle={toggleBulkActionStatusCampaignModal}
                        onUpdateData={handleUpdateData}
                    />
                )}
            </div>
        </React.Fragment>
    );
};

export default withRouter(Index);